import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import hotelImg from "../assests/images/hotel-bg.jpg";
import Hero from "../components/Hero";

import {
    FaHeart,
    FaRegHeart,
    FaMapMarkerAlt,
    FaStar,
    FaStarHalfAlt,
    FaRegStar,
    FaSearch,
    FaMoneyBillWave,
    FaMapMarkedAlt,
    FaUndo,
    FaSlidersH,
    FaChevronDown,
    FaArrowRight,
    FaSwimmingPool,
    FaWifi,
    FaUtensils,
    FaHotel,
    FaCheck,
    FaTimes,
} from "react-icons/fa";

export default function HotelList({
    limit,
    showViewAllButton = false,
    showSidebar = true,
    showBanner = false,
    showHeading = false,
}) {
    const [hotels, setHotels] = useState([]);
    const [loading, setLoading] = useState(true);

    const location = useLocation();
    const navigate = useNavigate();

    const [wishlist, setWishlist] = useState([]);

    const [search, setSearch] = useState("");
    const [minPrice, setMinPrice] = useState(0);
    const [maxPrice, setMaxPrice] = useState(5000);
    const [rating, setRating] = useState(0);

    const [destinations, setDestinations] = useState([]);
    const [selectedDestinations, setSelectedDestinations] = useState([]);

    const [debouncedSearch, setDebouncedSearch] = useState(search);
    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

    useEffect(() => {
        const storedWishlist =
            JSON.parse(localStorage.getItem("wishlist")) || [];

        setWishlist(storedWishlist);
    }, []);

    const updateWishlist = (updated) => {
        setWishlist(updated);
        localStorage.setItem("wishlist", JSON.stringify(updated));
    };

    const toggleWishlist = (hotel) => {
        const exists = wishlist.find((item) => item.id === hotel.id);

        const updated = exists
            ? wishlist.filter((item) => item.id !== hotel.id)
            : [...wishlist, hotel];

        updateWishlist(updated);
    };

    const isWishlisted = (id) =>
        wishlist.some((item) => item.id === id);

    const fetchDestinations = async () => {
        try {
            const res = await axios.get(
                "https://beachhoteldubai.com/api/destinations"
            );

            setDestinations(res.data);
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        fetchDestinations();
    }, []);

    const handleDestinationChange = (dest) => {
        setSelectedDestinations((prev) =>
            prev.some((d) => d.id === dest.id)
                ? prev.filter((d) => d.id !== dest.id)
                : [...prev, dest]
        );
    };

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(search);
        }, 500);

        return () => clearTimeout(handler);
    }, [search]);

    const fetchHotels = async () => {
        setLoading(true);

        try {
            const res = await axios.get(
                "https://beachhoteldubai.com/api/hotels",
                {
                    params: {
                        search: debouncedSearch,
                        destinations: selectedDestinations.map(
                            (d) => d.id
                        ),
                        minPrice,
                        maxPrice,
                        rating,
                    },
                }
            );

            setHotels(res.data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchHotels();
    }, [
        debouncedSearch,
        minPrice,
        maxPrice,
        rating,
        selectedDestinations,
    ]);

    const renderStars = (ratingValue) => {
        return Array.from({ length: 5 }, (_, i) => {
            if (i < Math.floor(ratingValue)) {
                return <FaStar key={i} />;
            }

            if (
                i === Math.floor(ratingValue) &&
                ratingValue % 1 >= 0.5
            ) {
                return <FaStarHalfAlt key={i} />;
            }

            return <FaRegStar key={i} />;
        });
    };

    const isFilterChanged =
        search !== "" ||
        minPrice !== 0 ||
        maxPrice !== 5000 ||
        rating !== 0 ||
        selectedDestinations.length > 0;

    const resetFilters = () => {
        setSearch("");
        setMinPrice(0);
        setMaxPrice(5000);
        setRating(0);
        setSelectedDestinations([]);
    };

    const activeFilterCount =
        (search ? 1 : 0) +
        (minPrice !== 0 || maxPrice !== 5000 ? 1 : 0) +
        (rating !== 0 ? 1 : 0) +
        (selectedDestinations.length > 0 ? 1 : 0);

    const inputClass = `
        w-full rounded-xl border border-[#E3E8E8]
        bg-[#F8FAFA] py-3.5 px-4
        text-sm font-medium text-[#172B35]
        outline-none transition-all duration-300
        placeholder:text-[#98A5AA]
        focus:border-[#8B0000] focus:bg-white
        focus:ring-4 focus:ring-[#8B0000]/[0.07]
    `;

    return (
        <div className="min-h-screen bg-[#F5F8FA]">

            {/* HERO */}

            {showBanner && (
                <div className="relative">

                    <Hero
                        variant="inner"
                        title="Find Your Perfect Hotel"
                        subtitle="Discover exceptional stays across Dubai Marina"
                        backgroundImage={hotelImg}
                    />

                    <div className="relative z-20 mx-auto -mt-10 max-w-[1536px] px-5 sm:px-8 lg:px-12 xl:px-16">
                        <div className="rounded-2xl border border-[#E4E9E9] bg-white p-3 shadow-[0_20px_60px_rgba(23,43,53,0.12)]">

                            <div className="flex flex-col gap-3 md:flex-row">

                                <div className="relative flex-1">
                                    <FaSearch className="absolute left-5 top-1/2 -translate-y-1/2 text-[#8B0000]" />

                                    <input
                                        type="text"
                                        value={search}
                                        onChange={(e) =>
                                            setSearch(e.target.value)
                                        }
                                        placeholder="Search hotels, areas or destinations..."
                                        className={`${inputClass} py-4 pl-12`}
                                    />
                                </div>

                                <button
                                    onClick={() =>
                                        setMobileFiltersOpen(true)
                                    }
                                    className="flex items-center justify-center gap-2 rounded-xl bg-[#8B0000] px-6 py-4 text-sm font-bold text-white transition hover:bg-[#690000] md:hidden"
                                >
                                    <FaSlidersH />
                                    Filters

                                    {activeFilterCount > 0 && (
                                        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#D7A74A] text-[10px] font-bold text-[#172B35]">
                                            {activeFilterCount}
                                        </span>
                                    )}
                                </button>

                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* MAIN CONTAINER */}

            <div
                className={`mx-auto w-full max-w-[1536px] ${showBanner
                    ? "pb-16 pt-12 px-6"
                    : "py-10 sm:py-14 lg:py-16"
                    }`}
            >

                {/* PAGE HEADING */}

                {(showHeading || location.pathname === "/hotels") && (
                    <div className="mb-10">

                        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">

                            <div>
                                <div className="mb-4 flex items-center gap-3">
                                    <span className="h-px w-8 bg-[#D7A74A]" />

                                    <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#8B0000]">
                                        Dubai Marina Collection
                                    </span>
                                </div>

                                <h1 className="font-serif text-[34px] font-semibold leading-[1.08] tracking-tight text-[#172B35] sm:text-[42px] lg:text-[48px]">
                                    Find Your Perfect

                                    <span className="block italic text-[#8B0000]">
                                        Stay in Dubai
                                    </span>
                                </h1>

                                <p className="mt-4 max-w-2xl text-sm leading-7 text-[#71808A] sm:text-base">
                                    Explore exceptional hotels, luxury
                                    resorts and beautiful stays across
                                    Dubai's most desirable destinations.
                                </p>
                            </div>

                            <div className="rounded-2xl border border-[#E3E8E8] bg-white px-5 py-4 shadow-sm">
                                <span className="font-serif text-2xl font-semibold text-[#8B0000]">
                                    {hotels.length}
                                </span>

                                <span className="ml-2 text-xs font-semibold text-[#71808A]">
                                    Hotels Available
                                </span>
                            </div>

                        </div>

                        <div className="mt-8 flex flex-wrap items-center gap-3">

                            {selectedDestinations.length > 0 && (
                                <div className="flex items-center gap-2 rounded-full border border-[#8B0000]/15 bg-[#8B0000]/[0.05] px-4 py-2.5 text-xs font-semibold text-[#8B0000]">
                                    <FaMapMarkerAlt />
                                    {selectedDestinations.length} destinations
                                </div>
                            )}

                            {rating > 0 && (
                                <div className="flex items-center gap-2 rounded-full border border-[#D7A74A]/40 bg-[#D7A74A]/10 px-4 py-2.5 text-xs font-semibold text-[#8A6900]">
                                    <FaStar />
                                    {rating}+ rating
                                </div>
                            )}

                            {isFilterChanged && (
                                <button
                                    onClick={resetFilters}
                                    className="flex items-center gap-2 rounded-full px-4 py-2.5 text-xs font-semibold text-[#71808A] transition hover:bg-white hover:text-[#8B0000]"
                                >
                                    <FaTimes />
                                    Clear filters
                                </button>
                            )}

                        </div>
                    </div>
                )}

                {/* MOBILE FILTER BUTTON */}

                {!showBanner && (
                    <div className="mb-6 md:hidden">
                        <button
                            onClick={() => setMobileFiltersOpen(true)}
                            className="flex w-full items-center justify-between rounded-2xl border border-[#E3E8E8] bg-white px-5 py-4 shadow-sm"
                        >
                            <span className="flex items-center gap-3 text-sm font-bold text-[#172B35]">
                                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#8B0000]/[0.06] text-[#8B0000]">
                                    <FaSlidersH />
                                </span>
                                Filter Hotels
                            </span>

                            <span className="flex items-center gap-2">
                                {activeFilterCount > 0 && (
                                    <span className="flex h-6 min-w-6 items-center justify-center rounded-full bg-[#8B0000] px-1.5 text-[10px] font-black text-white">
                                        {activeFilterCount}
                                    </span>
                                )}

                                <FaChevronDown className="text-xs text-[#71808A]" />
                            </span>
                        </button>
                    </div>
                )}

                {/* MAIN LAYOUT */}

                <div className="flex flex-col gap-8 lg:flex-row">

                    {/* SIDEBAR */}

                    {showSidebar && (
                        <aside
                            className={`fixed inset-0 z-[100] ${mobileFiltersOpen ? "block" : "hidden"
                                } lg:relative lg:inset-auto lg:z-auto lg:block lg:w-[290px] lg:flex-shrink-0`}
                        >

                            <div
                                onClick={() => setMobileFiltersOpen(false)}
                                className="absolute inset-0 bg-[#172B35]/60 backdrop-blur-sm lg:hidden"
                            />

                            <div className="absolute bottom-0 left-0 top-0 w-[88%] overflow-y-auto bg-white shadow-2xl sm:w-[360px] lg:relative lg:top-0 lg:w-full lg:overflow-visible lg:rounded-2xl lg:border lg:border-[#E3E8E8] lg:shadow-[0_10px_35px_rgba(23,43,53,0.05)]">

                                {/* FILTER HEADER */}

                                <div className="sticky top-0 z-10 flex items-center justify-between border-b border-[#E9EEEE] bg-white px-5 py-5 lg:rounded-t-2xl">

                                    <div className="flex items-center gap-3">
                                        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#8B0000] text-[#D7A74A]">
                                            <FaSlidersH />
                                        </span>

                                        <div>
                                            <h3 className="font-serif text-lg font-semibold text-[#172B35]">
                                                Filter Hotels
                                            </h3>

                                            <p className="mt-0.5 text-[11px] text-[#71808A]">
                                                Find your ideal stay
                                            </p>
                                        </div>
                                    </div>

                                    <button
                                        onClick={() =>
                                            setMobileFiltersOpen(false)
                                        }
                                        className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#F5F8FA] text-[#71808A] transition hover:bg-[#8B0000] hover:text-white lg:hidden"
                                    >
                                        <FaTimes />
                                    </button>
                                </div>

                                <div className="p-5">

                                    {/* SEARCH */}

                                    <div className="mb-7">
                                        <label className="mb-3 block text-[10px] font-bold uppercase tracking-[0.18em] text-[#71808A]">
                                            Search
                                        </label>

                                        <div className="relative">
                                            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8B0000]" />

                                            <input
                                                type="text"
                                                value={search}
                                                onChange={(e) =>
                                                    setSearch(e.target.value)
                                                }
                                                placeholder="Hotel or destination"
                                                className={`${inputClass} pl-11`}
                                            />
                                        </div>
                                    </div>

                                    {/* PRICE */}

                                    <div className="mb-7 border-t border-[#E9EEEE] pt-6">
                                        <div className="mb-3 flex items-center justify-between">
                                            <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#71808A]">
                                                Price Range
                                            </label>

                                            <FaMoneyBillWave className="text-sm text-[#D7A74A]" />
                                        </div>

                                        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">

                                            <div className="relative">
                                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[9px] font-bold text-[#98A5AA]">
                                                    AED
                                                </span>

                                                <input
                                                    type="number"
                                                    value={minPrice}
                                                    onChange={(e) =>
                                                        setMinPrice(e.target.value)
                                                    }
                                                    placeholder="Min"
                                                    className={`${inputClass} py-3 pl-11 pr-2 text-xs`}
                                                />
                                            </div>

                                            <span className="text-[#B9C3C7]">—</span>

                                            <div className="relative">
                                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[9px] font-bold text-[#98A5AA]">
                                                    AED
                                                </span>

                                                <input
                                                    type="number"
                                                    value={maxPrice}
                                                    onChange={(e) =>
                                                        setMaxPrice(e.target.value)
                                                    }
                                                    placeholder="Max"
                                                    className={`${inputClass} py-3 pl-11 pr-2 text-xs`}
                                                />
                                            </div>

                                        </div>
                                    </div>

                                    {/* RATING */}

                                    <div className="mb-7 border-t border-[#E9EEEE] pt-6">
                                        <div className="mb-3 flex items-center justify-between">
                                            <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#71808A]">
                                                Hotel Rating
                                            </label>

                                            <FaStar className="text-sm text-[#D7A74A]" />
                                        </div>

                                        <div className="relative">
                                            <select
                                                value={rating}
                                                onChange={(e) =>
                                                    setRating(e.target.value)
                                                }
                                                className={`${inputClass} appearance-none pr-10`}
                                            >
                                                <option value="0">All Ratings</option>
                                                <option value="3">3★ & above</option>
                                                <option value="4">4★ & above</option>
                                                <option value="4.5">4.5★ & above</option>
                                            </select>

                                            <FaChevronDown className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[10px] text-[#71808A]" />
                                        </div>
                                    </div>

                                    {/* DESTINATIONS */}

                                    <div className="border-t border-[#E9EEEE] pt-6">
                                        <div className="mb-4 flex items-center justify-between">
                                            <label className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.18em] text-[#71808A]">
                                                <FaMapMarkedAlt className="text-[#8B0000]" />
                                                Destinations
                                            </label>

                                            {selectedDestinations.length > 0 && (
                                                <span className="rounded-full bg-[#8B0000] px-2.5 py-1 text-[9px] font-black text-white">
                                                    {selectedDestinations.length}
                                                </span>
                                            )}
                                        </div>

                                        <div className="max-h-60 space-y-1.5 overflow-y-auto pr-1">
                                            {destinations.map((dest) => {
                                                const isChecked =
                                                    selectedDestinations.some(
                                                        (d) => d.id === dest.id
                                                    );

                                                return (
                                                    <label
                                                        key={dest.id}
                                                        className={`flex cursor-pointer items-center justify-between gap-3 rounded-xl border px-3 py-3 transition-all ${isChecked
                                                            ? "border-[#8B0000]/25 bg-[#8B0000]/[0.05]"
                                                            : "border-transparent bg-[#F5F8FA] hover:border-[#E3E8E8] hover:bg-white"
                                                            }`}
                                                    >
                                                        <div className="flex min-w-0 items-center gap-3">
                                                            <span
                                                                className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg ${isChecked
                                                                    ? "bg-[#8B0000] text-white"
                                                                    : "bg-white text-[#C8D0D3]"
                                                                    }`}
                                                            >
                                                                {isChecked && (
                                                                    <FaCheck className="text-[9px]" />
                                                                )}
                                                            </span>

                                                            <span
                                                                className={`truncate text-xs font-semibold ${isChecked
                                                                    ? "text-[#8B0000]"
                                                                    : "text-[#71808A]"
                                                                    }`}
                                                            >
                                                                {dest.name}
                                                            </span>
                                                        </div>

                                                        <input
                                                            type="checkbox"
                                                            checked={isChecked}
                                                            onChange={() =>
                                                                handleDestinationChange(dest)
                                                            }
                                                            className="sr-only"
                                                        />
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* RESET */}

                                    {isFilterChanged && (
                                        <div className="mt-7 border-t border-[#E9EEEE] pt-6">
                                            <button
                                                onClick={resetFilters}
                                                className="flex w-full items-center justify-center gap-2 rounded-xl border border-[#E3E8E8] bg-white py-3.5 text-xs font-bold text-[#71808A] transition hover:border-[#8B0000]/30 hover:bg-[#8B0000]/[0.04] hover:text-[#8B0000]"
                                            >
                                                <FaUndo />
                                                Reset All Filters
                                            </button>
                                        </div>
                                    )}

                                </div>
                            </div>
                        </aside>
                    )}

                    {/* HOTEL RESULTS */}

                    <main className="min-w-0 flex-1">

                        {/* RESULTS BAR */}

                        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#71808A]">
                                    Hotel Collection
                                </p>

                                <h2 className="mt-1 font-serif text-2xl font-semibold text-[#172B35] sm:text-3xl">
                                    {hotels.length}{" "}

                                    <span className="font-sans text-sm font-medium text-[#71808A]">
                                        stays found
                                    </span>
                                </h2>
                            </div>

                            {isFilterChanged && (
                                <div className="flex items-center gap-2">
                                    <span className="rounded-full border border-[#D7A74A]/40 bg-[#D7A74A]/10 px-3 py-1.5 text-[10px] font-bold text-[#8A6900]">
                                        {activeFilterCount} active filter
                                        {activeFilterCount > 1 ? "s" : ""}
                                    </span>

                                    <button
                                        onClick={resetFilters}
                                        className="text-xs font-semibold text-[#71808A] transition hover:text-[#8B0000]"
                                    >
                                        Clear
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* LOADING */}

                        {loading ? (
                            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
                                {[1, 2, 3, 4, 5, 6].map((item) => (
                                    <div
                                        key={item}
                                        className="overflow-hidden rounded-2xl border border-[#E3E8E8] bg-white shadow-sm"
                                    >
                                        <div className="h-56 animate-pulse bg-[#E8EEEE]" />

                                        <div className="space-y-4 p-5">
                                            <div className="h-5 w-3/4 animate-pulse rounded bg-[#E8EEEE]" />
                                            <div className="h-3 w-1/2 animate-pulse rounded bg-[#F0F3F4]" />
                                            <div className="h-12 w-full animate-pulse rounded-xl bg-[#F0F3F4]" />
                                            <div className="h-10 w-full animate-pulse rounded-xl bg-[#E8EEEE]" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : hotels.length === 0 ? (

                            /* EMPTY STATE */

                            <div className="rounded-3xl border border-[#E3E8E8] bg-white px-6 py-20 text-center shadow-sm">
                                <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full border border-[#D7A74A]/40 bg-[#D7A74A]/10 text-3xl text-[#8B0000]">
                                    <FaHotel />
                                </div>

                                <h3 className="mt-6 font-serif text-2xl font-semibold text-[#172B35]">
                                    No hotels found
                                </h3>

                                <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-[#71808A]">
                                    We couldn't find any hotels matching
                                    your current filters. Try changing your
                                    search or resetting the filters.
                                </p>

                                <button
                                    onClick={resetFilters}
                                    className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[#8B0000] px-6 py-3.5 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-[#690000]"
                                >
                                    <FaUndo />
                                    Reset Filters
                                </button>
                            </div>

                        ) : (

                            /* HOTEL GRID */

                            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
                                {(limit ? hotels.slice(0, limit) : hotels).map(
                                    (hotel) => (
                                        <article
                                            key={hotel.id}
                                            className="group flex flex-col overflow-hidden rounded-2xl border border-[#E3E8E8] bg-white shadow-[0_8px_35px_rgba(23,43,53,0.05)] transition-all duration-500 hover:-translate-y-1 hover:border-[#8B0000]/20 hover:shadow-[0_25px_60px_rgba(23,43,53,0.12)]"
                                        >

                                            {/* IMAGE */}

                                            <div
                                                className="relative h-[245px] cursor-pointer overflow-hidden"
                                                onClick={() =>
                                                    navigate(`/hotels/${hotel.slug}`)
                                                }
                                            >
                                                <img
                                                    src={`https://uaediscountedhotels.com/uploads/hotels/${hotel.image}`}
                                                    alt={hotel.name}
                                                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                                                />

                                                <div className="absolute inset-0 bg-gradient-to-t from-[#172B35]/85 via-[#172B35]/10 to-transparent opacity-90" />

                                                <div className="absolute left-4 top-4">
                                                    <span className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-[#172B35]/85 px-3 py-1.5 text-[9px] font-bold uppercase tracking-[0.12em] text-white shadow-lg backdrop-blur-md">
                                                        <FaHotel className="text-[#D7A74A]" />
                                                        Dubai Stay
                                                    </span>
                                                </div>

                                                {/* WISHLIST */}

                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        toggleWishlist(hotel);
                                                    }}
                                                    className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full border border-white/40 bg-white/95 text-[#71808A] shadow-lg backdrop-blur-md transition-all duration-300 hover:scale-110 hover:text-[#8B0000]"
                                                    aria-label="Add to wishlist"
                                                >
                                                    {isWishlisted(hotel.id) ? (
                                                        <FaHeart className="text-[#8B0000]" />
                                                    ) : (
                                                        <FaRegHeart />
                                                    )}
                                                </button>

                                                {/* LOCATION */}

                                                <div className="absolute bottom-4 left-4">
                                                    <div className="flex items-center gap-2 rounded-xl border border-white/30 bg-white/95 px-3 py-2 shadow-lg backdrop-blur-md">
                                                        <FaMapMarkerAlt className="text-xs text-[#8B0000]" />

                                                        <span className="max-w-[150px] truncate text-[11px] font-bold text-[#172B35]">
                                                            {hotel.location || "Dubai, UAE"}
                                                        </span>
                                                    </div>
                                                </div>

                                                {/* RATING */}

                                                <div className="absolute bottom-4 right-4">
                                                    <div className="flex items-center gap-2 rounded-xl bg-[#D7A74A] px-3 py-2 text-[#172B35] shadow-lg">
                                                        <FaStar className="text-[11px]" />

                                                        <span className="text-xs font-black">
                                                            {hotel.rating || "4.5"}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* CONTENT */}

                                            <div className="flex flex-1 flex-col p-5">

                                                <h3
                                                    onClick={() =>
                                                        navigate(`/hotels/${hotel.slug}`)
                                                    }
                                                    className="line-clamp-1 cursor-pointer font-serif text-xl font-semibold text-[#172B35] transition-colors duration-300 hover:text-[#8B0000]"
                                                >
                                                    {hotel.name}
                                                </h3>

                                                {/* STARS */}

                                                <div className="mt-2 flex items-center gap-2">
                                                    <div className="flex gap-0.5 text-[11px] text-[#D7A74A]">
                                                        {renderStars(hotel.rating || 4.5)}
                                                    </div>

                                                    <span className="text-[10px] font-medium text-[#98A5AA]">
                                                        Hotel rating
                                                    </span>
                                                </div>

                                                {/* DESCRIPTION */}

                                                <p className="mt-3 line-clamp-2 min-h-[42px] text-xs leading-6 text-[#71808A]">
                                                    {hotel.short_description?.replace(
                                                        /<[^>]*>/g,
                                                        ""
                                                    ) ||
                                                        "Enjoy a comfortable and memorable stay in one of Dubai's exceptional hotels."}
                                                </p>

                                                {/* AMENITIES */}

                                                <div className="mt-4 grid grid-cols-3 gap-2 border-y border-[#E9EEEE] py-4">

                                                    <div className="flex flex-col items-center gap-1.5 rounded-xl bg-[#F5F8FA] py-2.5 transition group-hover:bg-[#8B0000]/[0.04]">
                                                        <FaSwimmingPool className="text-sm text-[#8B0000]" />
                                                        <span className="text-[9px] font-semibold text-[#71808A]">
                                                            Pool
                                                        </span>
                                                    </div>

                                                    <div className="flex flex-col items-center gap-1.5 rounded-xl bg-[#F5F8FA] py-2.5 transition group-hover:bg-[#8B0000]/[0.04]">
                                                        <FaWifi className="text-sm text-[#8B0000]" />
                                                        <span className="text-[9px] font-semibold text-[#71808A]">
                                                            WiFi
                                                        </span>
                                                    </div>

                                                    <div className="flex flex-col items-center gap-1.5 rounded-xl bg-[#F5F8FA] py-2.5 transition group-hover:bg-[#8B0000]/[0.04]">
                                                        <FaUtensils className="text-sm text-[#8B0000]" />
                                                        <span className="text-[9px] font-semibold text-[#71808A]">
                                                            Restaurant
                                                        </span>
                                                    </div>

                                                </div>

                                                {/* PRICE + BUTTON */}

                                                <div className="mt-auto flex items-end justify-between gap-3 pt-5">

                                                    <div>
                                                        <p className="text-[9px] font-bold uppercase tracking-[0.12em] text-[#98A5AA]">
                                                            Starting from
                                                        </p>

                                                        <div className="mt-0.5 flex items-baseline gap-1">
                                                            <span className="text-[10px] font-bold text-[#71808A]">
                                                                AED
                                                            </span>

                                                            <span className="font-serif text-2xl font-semibold text-[#172B35]">
                                                                {Math.round(hotel.price_per_night)}
                                                            </span>
                                                        </div>

                                                        <p className="text-[9px] text-[#98A5AA]">
                                                            per night
                                                        </p>
                                                    </div>

                                                    <button
                                                        onClick={() =>
                                                            navigate(`/hotels/${hotel.slug}`)
                                                        }
                                                        className="group/view flex items-center gap-2 rounded-xl bg-[#8B0000] px-4 py-3 text-[11px] font-bold text-white shadow-[0_8px_20px_rgba(139,0,0,0.14)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#690000]"
                                                    >
                                                        View Hotel

                                                        <FaArrowRight className="text-[9px] transition-transform duration-300 group-hover/view:translate-x-1" />
                                                    </button>

                                                </div>
                                            </div>

                                            {/* BOTTOM ACCENT */}

                                            <div className="h-[2px] w-0 bg-[#D7A74A] transition-all duration-500 group-hover:w-full" />

                                        </article>
                                    )
                                )}
                            </div>
                        )}

                        {/* MOBILE VIEW ALL */}

                        {(showHeading || showViewAllButton) && (
                            <div className="mt-8 sm:hidden">
                                <button
                                    onClick={() => navigate("/hotels")}
                                    className="flex w-full items-center justify-center gap-3 rounded-xl bg-[#8B0000] py-4 text-sm font-bold text-white shadow-lg transition hover:bg-[#690000]"
                                >
                                    View All Hotels
                                    <FaArrowRight className="text-xs" />
                                </button>
                            </div>
                        )}

                    </main>
                </div>
            </div>
        </div>
    );
}